import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Link,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { accountService } from '../../services/api/accountService';
import { businessService } from '../../services/api/businessService';
import { messagingService } from '../../services/api/messagingService';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import {
  SignupPageWrapper,
  HeroBanner,
  HeroTitle,
  HeroSubtitle,
  FormCard,
  PrimaryButton,
  SuccessWrapper,
  SuccessIcon,
  SuccessTitle,
  SuccessMessage,
} from '../ProviderSignup/ProviderSignup.styled';
import { ResultsCard, FooterHint } from './JoinExistingBusiness.styled';

const JOIN_RETURN_PATH = '/join-existing-business';

function businessLocationLine(b) {
  const addr = b?.BusinessAddress;
  return [addr?.City, addr?.State].filter(Boolean).join(', ');
}

const JoinExistingBusiness = () => {
  const { user, loading: authLoading } = useAuth();
  const [account, setAccount] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!user || authLoading) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await accountService.getAccount();
        if (!cancelled) setAccount(data);
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setListLoading(true);
        const data = await businessService.getAllBusinesses(false);
        if (!cancelled) {
          setBusinesses(Array.isArray(data) ? data : []);
          setLoadError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.message || 'Could not load businesses.');
          setBusinesses([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return businesses
      .filter((b) =>
        (b.BusinessName || '').toLowerCase().includes(q),
      )
      .slice(0, 40);
  }, [businesses, query]);

  const handleSubmit = async () => {
    if (!selected || !account) return;
    setSubmitError(null);
    const email = (account.Email || user?.Email || '').trim();
    if (!email) {
      setSubmitError('No email on your account. Add an email and try again.');
      return;
    }

    setSubmitting(true);
    try {
      await messagingService.submitJoinBusinessRequest({
        email,
        businessId: selected.BusinessId,
      });
      setDone(true);
    } catch (e) {
      setSubmitError(
        e.message || 'Something went wrong sending your request. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <SignupPageWrapper>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '56vh',
          }}
        >
          <NkySpinner />
        </Box>
      </SignupPageWrapper>
    );
  }

  if (!user) {
    return (
      <Navigate to="/login" replace state={{ from: JOIN_RETURN_PATH }} />
    );
  }

  if (done) {
    return (
      <SignupPageWrapper>
        <HeroBanner>
          <HeroTitle as="h1">Request sent</HeroTitle>
          <HeroSubtitle>
            Our team will follow up to link your account to the business you
            selected.
          </HeroSubtitle>
        </HeroBanner>
        <FormCard>
          <SuccessWrapper>
            <SuccessIcon aria-hidden>✓</SuccessIcon>
            <SuccessTitle as="h2">You&apos;re all set for now</SuccessTitle>
            <SuccessMessage>
              Thanks — we emailed our team (including support@nkyconnect.com)
              with your account details and the business you chose. Someone
              will finish adding you to that business profile.
            </SuccessMessage>
            <Box sx={{ mt: 1 }}>
              <Link
                component={RouterLink}
                to="/"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: '#0d4efe',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Back to home
              </Link>
            </Box>
          </SuccessWrapper>
        </FormCard>
      </SignupPageWrapper>
    );
  }

  return (
    <SignupPageWrapper>
      <HeroBanner>
        <HeroTitle as="h1">Join an existing business</HeroTitle>
        <HeroSubtitle>
          Search by name, pick the right listing, then send a request. Access
          is not updated until our team links you.
        </HeroSubtitle>
      </HeroBanner>

      <FormCard>
        {loadError && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            {loadError} You can try again later or{' '}
            <Link href="/contact" sx={{ fontWeight: 600 }}>
              contact us
            </Link>
            .
          </Alert>
        )}

        <TextField
          label="Search by business name"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(null);
          }}
          fullWidth
          autoComplete="off"
          placeholder="e.g. River City Plumbing"
          disabled={listLoading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              background: '#fafbfd',
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.75, color: '#888', fontSize: '0.8rem' }}
        >
          Type at least 2 characters to see matches.
        </Typography>

        {listLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <NkySpinner />
          </Box>
        ) : (
          <ResultsCard>
            {filtered.length === 0 ? (
              <Box sx={{ p: 2.5 }}>
                <Typography sx={{ color: '#555', fontSize: '0.92rem' }}>
                  {query.trim().length < 2
                    ? 'Start typing a business name.'
                    : 'No matches — try a shorter or different search.'}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filtered.map((b) => (
                  <ListItemButton
                    key={
                      b.BusinessId || `${b.BusinessEmail}-${b.BusinessName}`
                    }
                    selected={
                      selected?.BusinessId === b.BusinessId &&
                      selected?.BusinessEmail === b.BusinessEmail
                    }
                    onClick={() => setSelected(b)}
                    sx={{
                      py: 1.25,
                      borderBottom: '1px solid #eef1f6',
                      '&:last-of-type': { borderBottom: 'none' },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(13, 78, 254, 0.08)',
                        borderLeft: '3px solid #01ebb2',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(13, 78, 254, 0.12)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={b.BusinessName}
                      secondary={businessLocationLine(b) || undefined}
                      primaryTypographyProps={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontWeight: 600,
                        fontSize: '0.95rem',
                      }}
                      secondaryTypographyProps={{ fontSize: '0.82rem' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </ResultsCard>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {submitError}
          </Alert>
        )}

        <PrimaryButton
          type="button"
          style={{ width: '100%', marginTop: 20 }}
          disabled={!selected || submitting || listLoading}
          onClick={handleSubmit}
        >
          {submitting ? 'Sending request…' : 'Submit join request'}
        </PrimaryButton>

        <FooterHint>
          Wrong place?{' '}
          <RouterLink to="/provider-signup">Enroll a new business</RouterLink>
        </FooterHint>
      </FormCard>
    </SignupPageWrapper>
  );
};

export default JoinExistingBusiness;
